<script lang="ts" setup>
  import { computed } from 'vue';
  import { useVariantStock } from '@storefront/storefront';
  import { useUnitFormatter, Unit } from '@othercommerce/domain';
  import { useTranslator } from '@othercommerce/foundation';
  import type { StockMeta } from '@storefront/resources';

  interface Inventory {
    id: number;
    stockOnOrder: boolean;
    stockAvailable: number;
    stockUnit: Unit,
    stockMeta: StockMeta;
    disableSalesOverSupply: boolean;
  }

  interface Props {
    variant: Inventory;
  }

  const props = defineProps<Props>();
  const translator = useTranslator();
  const formatter = useUnitFormatter();
  const { stockOnOrder, inStock, amountInCart } = useVariantStock(() => props.variant);

  const availabilityMessage = computed(() => {

    if (!stockOnOrder.value) {
      if (inStock.value) {
        return translator.trans('SHOP.COMMON.LABEL.STOCK_LEVEL_AVAILABLE_STRONG');
      } else {
        return translator.trans('SHOP.COMMON.LABEL.STOCK_LEVEL_UNAVAILABLE');
      }
    } else {
      return translator.trans('SHOP.COMMON.LABEL.STOCK_LEVEL_AVAILABLE_ON_ORDER');
    }
  });


  const inCartMessage = computed(() => {
    let unit = props.variant.stockUnit;
    let amount = amountInCart.value;

    if (amount) {
      return translator.trans('SHOP.COMMON.LABEL.STOCK_LEVEL_IN_CART', { amount: formatter.accusative(unit, amount) });
    }
  });
</script>
<template>
  <div class="stock" aria-live="polite">
    <div class="stock__availability">
      <span v-html="availabilityMessage" :class="{'available':  inStock}" class="stock__availability-message" />
      <span v-if="inCartMessage" v-html="inCartMessage" />
    </div>
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-stock-font-size: 0.75rem;

    @include media.query(md) {
      --sf-stock-font-size: 0.625rem;
    }
  }

  .stock {
    display: flex;
    align-items: flex-start;

    &__availability {
      display: flex;
      flex-direction: column;
      font-size: var(--sf-stock-font-size);
      line-height: 1.25;
    }

    &__availability-message {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &:before {
        content: "";
        background-color: #f87171;
        border: 1px solid #ef444488;
        box-shadow: 0 0 3px 1px #f87171fa;
        border-radius: 5px;
        height: 8px;
        width: 8px;
      }

      &.available {
        &:before {
          background-color: #a3e635;
          border-color: #84cc1688;
          box-shadow: 0 0 3px 1px #a3e635fa;
        }
      }
    }
  }
</style>
